import { useEffect, useState, useCallback } from "react";
import { Search as SearchIcon, SlidersHorizontal, Loader2, X } from "lucide-react";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const SORTS = [
  { v: "newest", l: "Yeni Eklenen" },
  { v: "price_asc", l: "Fiyat: Düşük → Yüksek" },
  { v: "price_desc", l: "Fiyat: Yüksek → Düşük" },
  { v: "gram_asc", l: "Gram: Düşük → Yüksek" },
  { v: "gram_desc", l: "Gram: Yüksek → Düşük" },
];

export default function Search() {
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [karat, setKarat] = useState("all");
  const [stock, setStock] = useState("all");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [sort, setSort] = useState("newest");
  const [showFilters, setShowFilters] = useState(false);
  const [color, setColor] = useState("");
  const [stone, setStone] = useState("");
  const [facets, setFacets] = useState({ colors: [], stones: [] });

  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
  }, []);

  useEffect(() => {
    const params = {};
    if (category !== "all") params.category = category;
    api.get("/products/facets", { params }).then((r) => setFacets(r.data || { colors: [], stones: [] })).catch(() => {});
  }, [category]);

  const load = useCallback(async () => {
    setLoading(true);
    const params = { sort };
    if (search) params.search = search;
    if (category !== "all") params.category = category;
    if (karat !== "all") params.karat = karat;
    if (stock !== "all") params.stock_status = stock;
    if (minPrice) params.min_price = minPrice;
    if (maxPrice) params.max_price = maxPrice;
    if (color) params.color = color;
    if (stone) params.stone = stone;
    try {
      const r = await api.get("/products", { params });
      setProducts(r.data);
    } finally {
      setLoading(false);
    }
  }, [search, category, karat, stock, minPrice, maxPrice, sort, color, stone]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const clearFilters = () => {
    setCategory("all"); setKarat("all"); setStock("all"); setMinPrice(""); setMaxPrice(""); setSort("newest"); setColor(""); setStone("");
  };

  const selCls = "bg-surface border-border text-foreground focus:ring-gold h-10";

  return (
    <div>
      <h1 className="font-display text-3xl text-foreground mb-4">Ürün Arama</h1>

      <div className="relative mb-3">
        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
        <Input
          data-testid="search-input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Ara: kod, ayar, kategori, alt kategori (örn. Yüzük, 22, Trabzon, OR-10245)"
          className="pl-10 bg-surface border-border text-foreground h-12 focus-visible:ring-gold focus-visible:border-gold"
        />
      </div>

      <div className="flex items-center gap-2 mb-4">
        <button
          data-testid="toggle-filters"
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-2 px-3 py-2 rounded-md border border-border text-sm text-muted-foreground hover:border-gold hover:text-gold transition-colors"
        >
          <SlidersHorizontal size={15} /> Filtreler
        </button>
        <div className="flex-1">
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger data-testid="sort-select" className={selCls}><SelectValue /></SelectTrigger>
            <SelectContent className="bg-surface border-border text-foreground">
              {SORTS.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {(facets.colors.length > 0 || facets.stones.length > 0) && (
        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="facet-chips">
          {facets.colors.map((c) => {
            const active = color === c.value;
            return (
              <button
                key={c.value}
                data-testid={`facet-color-${c.value}`}
                onClick={() => setColor(active ? "" : c.value)}
                className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${active ? "bg-gold text-obsidian border-gold" : "border-border text-muted-foreground hover:border-gold hover:text-gold"}`}
              >
                {c.label} <span className="opacity-60">({c.count})</span>
              </button>
            );
          })}
          {facets.colors.length > 0 && facets.stones.length > 0 && (
            <span className="w-px h-5 bg-border mx-1" />
          )}
          {facets.stones.map((s) => {
            const active = stone === s.value;
            return (
              <button
                key={s.value}
                data-testid={`facet-stone-${s.value}`}
                onClick={() => setStone(active ? "" : s.value)}
                className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${active ? "bg-gold text-obsidian border-gold" : "border-border text-muted-foreground hover:border-gold hover:text-gold"}`}
              >
                {s.label} <span className="opacity-60">({s.count})</span>
              </button>
            );
          })}
          {(color || stone) && (
            <button data-testid="facet-clear" onClick={() => { setColor(""); setStone(""); }} className="flex items-center gap-1 px-2 py-1.5 text-xs text-muted-foreground hover:text-red-400 transition-colors">
              <X size={12} /> Temizle
            </button>
          )}
        </div>
      )}

      {showFilters && (
        <div className="bg-surface border border-border rounded-md p-4 mb-4 grid grid-cols-2 sm:grid-cols-3 gap-3 animate-fade-up">
          <div>
            <label className="text-[11px] text-muted-foreground mb-1 block">Kategori</label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="filter-category" className={selCls}><SelectValue placeholder="Tümü" /></SelectTrigger>
              <SelectContent className="bg-surface border-border text-foreground max-h-64">
                <SelectItem value="all">Tümü</SelectItem>
                {categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] text-muted-foreground mb-1 block">Ayar</label>
            <Select value={karat} onValueChange={setKarat}>
              <SelectTrigger data-testid="filter-karat" className={selCls}><SelectValue placeholder="Tümü" /></SelectTrigger>
              <SelectContent className="bg-surface border-border text-foreground">
                <SelectItem value="all">Tümü</SelectItem>
                {["8", "14", "18", "22", "24"].map((k) => <SelectItem key={k} value={k}>{k} Ayar</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] text-muted-foreground mb-1 block">Stok</label>
            <Select value={stock} onValueChange={setStock}>
              <SelectTrigger data-testid="filter-stock" className={selCls}><SelectValue placeholder="Tümü" /></SelectTrigger>
              <SelectContent className="bg-surface border-border text-foreground">
                <SelectItem value="all">Tümü</SelectItem>
                <SelectItem value="Stokta">Stokta</SelectItem>
                <SelectItem value="Stokta Yok">Stokta Yok</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] text-muted-foreground mb-1 block">Min Fiyat</label>
            <Input data-testid="filter-min-price" type="number" value={minPrice} onChange={(e) => setMinPrice(e.target.value)} placeholder="0" className="bg-obsidian border-border text-foreground h-10 focus-visible:ring-gold" />
          </div>
          <div>
            <label className="text-[11px] text-muted-foreground mb-1 block">Max Fiyat</label>
            <Input data-testid="filter-max-price" type="number" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} placeholder="∞" className="bg-obsidian border-border text-foreground h-10 focus-visible:ring-gold" />
          </div>
          <div className="flex items-end">
            <button onClick={clearFilters} data-testid="clear-filters" className="flex items-center gap-1 text-sm text-muted-foreground hover:text-red-400 transition-colors h-10">
              <X size={14} /> Temizle
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 className="animate-spin text-gold" size={28} /></div>
      ) : products.length === 0 ? (
        <p className="text-muted-foreground text-sm py-12 text-center">Sonuç bulunamadı.</p>
      ) : (
        <>
          <p className="text-xs text-muted-foreground mb-3">{products.length} ürün bulundu</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </>
      )}
    </div>
  );
}
